/**
 * The deck screen's «Тексты» on the phone (handoff §8, artboard 10): the texts
 * linked to this deck, a full-width «Добавить текст» under them, and the deck's
 * own pinned study bar, which the segment switch leaves in place.
 *
 * It renders in the page's one scroll surface (ADR-067 Decision 3), in the slot
 * the card feed takes under «Карточки». The pinned bar and its reserve use the
 * feed's own two measurements (`useViewportBottomGap`, `useMeasuredHeight`), so
 * the bar sits where the feed's bar sits and the last row clears it.
 */
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import DeckViewBar from '../DeckViewBar'
import { useLocale } from '../../i18n/context'
import { useDeckTexts } from '../../hooks/useTexts'
import { useMeasuredHeight } from '../../hooks/useMeasuredHeight'
import { useViewportBottomGap } from '../../hooks/useViewportBottomGap'
import { useAddTextToDeck } from '../../hooks/useAddTextToDeck'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'
import type { TextListItem } from '../../types'
import DeckTextsEmpty from './DeckTextsEmpty'
import GappedText from './GappedText'
import { DeleteFlow, TextRowMenu } from './TextList'
import TextShareSheet from './share/TextShareSheet'
import { SecondaryPill } from './ui'

interface Props {
  deck: { id: string; title: string }
  /** The study bar is for a deck that has cards. */
  hasCards: boolean
  onStudy: () => void
}

const ROW_CSS = `.dt-row{cursor:pointer}.dt-row:focus-visible{outline:2px solid var(--accent);outline-offset:2px}`

export default function DeckTextsMobile({ deck, hasCards, onStudy }: Props) {
  const { tr } = useLocale()
  const t = tr.deckTexts
  // The phone's rows say how many of a text's words are already cards here.
  const { data, isLoading, isError, refetch } = useDeckTexts(deck.id, true)
  const addText = useAddTextToDeck(deck)
  const [deleting, setDeleting] = useState<TextListItem | null>(null)
  const [sharing, setSharing] = useState<string | null>(null)
  const bottomGap = useViewportBottomGap()
  const [barRef, barHeight] = useMeasuredHeight()

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ height: 112, borderRadius: 16, background: 'var(--bg-secondary)', opacity: 0.6 }} />
        ))}
      </div>
    )
  } else if (isError || !data) {
    body = (
      <div role="alert" style={{ textAlign: 'center', padding: '32px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{t.retry}</SecondaryPill>
      </div>
    )
  } else if (data.items.length === 0) {
    body = <DeckTextsEmpty onAdd={addText} />
  } else {
    body = (
      <>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.items.map((text) => (
            <li key={text.id}><DeckTextRow text={text} deckTitle={deck.title} onDelete={() => setDeleting(text)} onShare={() => setSharing(text.id)} /></li>
          ))}
        </ul>
        <SecondaryPill full height={44} onClick={addText} style={{ marginTop: 12 }}>
          <Plus size={16} aria-hidden />
          {t.addText}
        </SecondaryPill>
      </>
    )
  }

  return (
    <div data-testid="deck-texts" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu}>
      <style>{ROW_CSS}</style>
      {/* The pinned bar is out of flow; its measured height is reserved here so the
          last row and the add button scroll clear of it (ADR-067 Decision 4). */}
      <div style={{ paddingBottom: 4 + barHeight }}>{body}</div>
      {hasCards && (
        <div
          ref={barRef}
          data-deck-pinned=""
          style={{
            position: 'fixed', left: 0, right: 0, zIndex: 21,
            bottom: `calc(${bottomGap}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <DeckViewBar onStudy={onStudy} />
        </div>
      )}
      {deleting && <DeleteFlow text={deleting} onClose={() => setDeleting(null)} />}
      {sharing && <TextShareSheet textId={sharing} onClose={() => setSharing(null)} />}
    </div>
  )
}

/** Handoff §7's row without the deck meta and the tile: the preview runs to three lines. */
function DeckTextRow({ text, deckTitle, onDelete, onShare }: { text: TextListItem; deckTitle: string; onDelete: () => void; onShare: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { tr } = useLocale()
  // The text screen's back link names this deck.
  const open = () =>
    navigate(`/texts/${text.id}`, { state: { from: location.pathname + location.search, fromLabel: deckTitle } })
  const gaps = text.gap_count > 0 ? tr.textList.gaps(text.gap_count) : tr.textList.noGaps
  // Left out rather than guessed when the server could not work it out.
  const meta = text.words_in_deck == null ? gaps : `${gaps} · ${tr.deckTexts.wordsInDeck(text.words_in_deck)}`

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={text.title}
      data-testid="deck-text-row"
      className="dt-row"
      onClick={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
      }}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 4px 12px 14px', borderRadius: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{
          display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {text.title}
        </span>
        <span
          data-testid="deck-text-preview"
          style={{
            display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden',
            fontFamily: 'var(--font-content)', fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)',
          }}
        >
          <GappedText text={text.preview} spans={text.preview_gaps} blankWidth={40} blankTestId="deck-text-blank" />
        </span>
        <span data-testid="deck-text-meta" style={{ display: 'block', fontSize: 12, lineHeight: 1.4, color: 'var(--text-tertiary)' }}>
          {meta}
        </span>
      </div>
      <TextRowMenu
        title={text.title}
        onDelete={onDelete}
        onEdit={() => navigate(`/texts/${text.id}/edit`, { state: { from: location.pathname + location.search, fromLabel: deckTitle } })}
        onShare={onShare}
      />
    </div>
  )
}
